import * as React from 'react'
import InputField from 'app/shared/components/InputField'
import {
    InputSecureField,
    InputSecureFieldRef,
    InputSecureFieldReturn,
} from './InputSecureField'
import { InputBaseProps } from './InputTypes'
import ScrollPicker from './ScrollPicker'

export type { InputSecureFieldRef, InputSecureFieldReturn }

/**
 * 입력 형태 미확정
 */
type InputDLSecureFieldProps = Pick<
    InputBaseProps,
    'result' | 'resultId' | 'resultMsg'
> & {
    /**
     * 운전면허 지역
     */
    no0?: string
    /**
     * 운전면허 지역번호+발급년도 1자리
     */
    no1?: string
    /**
     * 발급년도 뒷1자리
     */
    no2?: string

    /**
     * 일련번호 뒤 6자리
     */
    no3?: string

    /**
     * 체크섬 1자리
     */
    no4?: string

    /**
     * 발급회차 1자리
     */
    no5?: string

    /**
     * API 명세서 따라 정의함(ath0203)
     * @param no1 - 운전면허 지역
     * @param no2 - 운전면허 지역번호+발급년도 1자리
     * @param no3 - 발급년도 뒷1자리
     * @param no4 - 일련번호 뒤 6자리
     * @param no5 - 체크섬 1자리
     * @param no6 - 발급회차 1자리
     * @param isFulfilled - 입력 완료 여부
     */
    onChange?(
        no1: string,
        no2: string,
        no3: string,
        no4: string,
        no5: string,
        no6: string,
        isFulfilled: boolean
    ): void
    /**
     * API 명세서 따라 정의함(ath0203)
     * @param no1 - 운전면허 지역
     * @param no2 - 운전면허 지역번호+발급년도 1자리
     * @param no3 - 발급년도 뒷1자리
     * @param no4 - 일련번호 뒤 6자리
     * @param no5 - 체크섬 1자리
     * @param no6 - 발급회차 1자리
     * @param isFulfilled - 입력 완료 여부
     */
    onClose?(
        no1: string,
        no2: string,
        no3: string,
        no4: string,
        no5: string,
        no6: string,
        isFulfilled: boolean
    ): void
}

export type InputDLSecureFieldRef = {
    /**
     * 입력된 값을 받는다. 제거될 수 있음... 사용하는 경우 문의
     * API 명세서 따라 정의함(ath0203)
     * index 0 - 운전면허 앞7자리 00-00-000111-22 중 0
     * index 1 - 운전면허 암호화1 3자리 00-00-000111-22 중 1
     * index 2 - 운전면허 암호화2 2자리 00-00-000111-22 중 2
     */
    getData(): string[]
    /**
     * 입력값을 암호화 한 후 받는다. 서버 전송시 사용
     * index 0 - string > 운전면허 앞7자리 00-00-000111-22 중 0
     * index 1 - InputSecureFieldReturn > 운전면허 암호화1 3자리 00-00-000111-22 중 1
     * index 2 - InputSecureFieldReturn > 운전면허 암호화2 2자리 00-00-000111-22 중 2
     */
    getSecureData(): (InputSecureFieldReturn | string)[]
    /**
     * 입력이 완료되었는지를 반환한다.
     */
    isFulfilled(): boolean
}

export const InputDLSecureField = React.forwardRef<
    InputDLSecureFieldRef,
    InputDLSecureFieldProps
>((props, ref) => {
    const {
        no0 = '',
        no1 = '',
        no2 = '',
        no3 = '',
        no4 = '',
        no5 = '',
        onChange,
        onClose,
        result,
        resultId,
        resultMsg = '운전면허번호가 맞지 않습니다. 확인해주세요.',
    } = props
    const ifRef0 = React.useRef<HTMLInputElement>(null) //운전면허 지역번호+발급년도 1자리
    const ifRef1 = React.useRef<InputSecureFieldRef>(null) //발급년도 뒷1자리
    const ifRef2 = React.useRef<InputSecureFieldRef>(null) //일련번호 뒤 6자리
    const ifRef3 = React.useRef<InputSecureFieldRef>(null) //체크섬 1자리
    const ifRef4 = React.useRef<HTMLInputElement>(null) //발급회차 1자리

    //운전면허 앞자리
    const licenseArea = [
        '11',
        '12',
        '13',
        '14',
        '15',
        '16',
        '17',
        '18',
        '19',
        '20',
        '21',
        '22',
        '23',
        '25',
        '26',
        '서울',
        '부산',
        '경기',
        '강원',
        '충북',
        '충남',
        '전북',
        '전남',
        '경북',
        '경남',
        '제주',
        '대구',
        '인천',
        '대전',
        '울산',
    ]

    //-- 피커 보이고 감추기
    const [licensePicker, setLicensePicker] = React.useState(false)
    //-- 피커 선택 값
    const [licenseSelected, setLicenseSelected] = React.useState(no0)
    //운전면허 지역번호+발급년도 1자리
    const [license1, setLicense1] = React.useState(no1)
    //발급년도 뒷1자리
    const [license2, setLicense2] = React.useState(no2)
    //일련번호 뒤 6자리
    const [license3, setLicense3] = React.useState(no3)
    //체크섬 1자리
    const [license4, setLicense4] = React.useState(no4)
    //발급회차 1자리
    const [license5, setLicense5] = React.useState(no5)

    React.useImperativeHandle(
        ref,
        () => ({
            getSecureData: () => {
                return [
                    licenseSelected,
                    ifRef0.current?.value,
                    ifRef1.current?.getSecureData(),
                    ifRef2.current?.getSecureData(),
                    ifRef3.current?.getSecureData(),
                    ifRef4.current?.value,
                ]
            },
            //제거될 수 있음... 사용하는 경우 문의
            getData: () => [
                licenseSelected,
                ifRef0.current?.value,
                ifRef1.current?.getData(),
                ifRef2.current?.getData(),
                ifRef3.current?.getData(),
                ifRef4.current?.value,
            ],
            isFulfilled: () => {
                const isFulfilled =
                    licenseSelected.length >= 2 &&
                    ifRef0.current?.value.length >= 1 &&
                    ifRef1.current.isFulfilled() &&
                    ifRef2.current.isFulfilled() &&
                    ifRef3.current.isFulfilled() &&
                    ifRef4.current?.value.length >= 1
                return isFulfilled
            },
        }),
        [
            licenseSelected,
            ifRef0.current,
            ifRef1.current,
            ifRef2.current,
            ifRef3.current,
            ifRef4.current,
        ]
    )

    const handleClose = () => {
        const sendNumber = licenseSelected
        const secureNumber1 = license1
        const secureNumber2 = ifRef1.current.getData()
        const secureNumber3 = ifRef2.current.getData()
        const secureNumber4 = ifRef3.current.getData()
        const secureNumber5 = license5
        const isFulfilled =
            sendNumber.length >= 2 &&
            secureNumber1.length >= 1 &&
            secureNumber2.length >= 1 &&
            secureNumber3.length >= 6 &&
            secureNumber4.length >= 1 &&
            secureNumber5.length >= 1
        onClose?.(
            sendNumber,
            secureNumber1,
            secureNumber2,
            secureNumber3,
            secureNumber4,
            secureNumber5,
            isFulfilled
        )
    }

    const handleChanged = (
        year?: string | null,
        no1?: string | null,
        no5?: string | null
    ) => {
        const sendNumber = licenseSelected
        const secureNumber1 = license1
        const secureNumber2 = ifRef1.current?.getData()
        const secureNumber3 = ifRef2.current?.getData()
        const secureNumber4 = ifRef3.current?.getData()
        const secureNumber5 = license5

        const isFulfilled =
            sendNumber.length >= 2 &&
            secureNumber1.length >= 1 &&
            secureNumber2.length >= 1 &&
            secureNumber3.length >= 6 &&
            secureNumber4.length >= 1 &&
            secureNumber5.length >= 1
        onChange?.(
            sendNumber,
            secureNumber1,
            secureNumber2,
            secureNumber3,
            secureNumber4,
            secureNumber5,
            isFulfilled
        )
    }

    const resultCheck = React.useMemo(() => {
        let rt = null
        if (result !== 'hidden' && resultMsg) {
            rt = (
                <p id={resultId} className={`p-msg-${result}`}>
                    {resultMsg}
                </p>
            )
        }
        return rt
    }, [result, resultMsg])

    React.useEffect(() => {
        setLicenseSelected(no0)
        setLicense1(no1)
        setLicense2(no2)
        setLicense3(no3)
        setLicense4(no4)
        setLicense5(no5)
    }, [no0, no1, no2, no3, no4, no5])

    return (
        <>
            <span className="inputs-label">운전면허번호</span>
            <div className="inputs-x-mix inputs-license">
                <ScrollPicker
                    mode="year"
                    years={licenseArea}
                    handlePlaceholder={licenseSelected}
                    handleClassName="input-basic -select"
                    dialogHeaderName="지역선택"
                    visible={licensePicker}
                    onOpen={() => setLicensePicker(true)}
                    onClose={() => setLicensePicker(false)}
                    onSelectYear={(year) => {
                        setLicenseSelected(year)
                        handleChanged(year)
                        ifRef0.current?.focus()
                    }}
                />
                <span className="bar px-5">-</span>
                <div className="second-area input01">
                    <InputField
                        ref={ifRef0}
                        value={license1}
                        maxLength={1}
                        onChange={(e) => {
                            setLicense1(e.target.value)

                            if (e.target.value.length >= 1) {
                                ifRef1.current?.focus()
                            }

                            handleChanged(null, e.target.value)
                        }}
                        title="최초 면허 발급 연도 앞 1자리"
                        className="input1"
                    />
                    <InputSecureField
                        id="dl_md1"
                        ref={ifRef1}
                        value={license2}
                        maxLength={1}
                        onChange={handleChanged}
                        onClose={(value, isFulfilled) => {
                            handleClose()
                            if (isFulfilled) {
                                ifRef2.current?.focus()
                            }
                        }}
                        title="최초 면허 발급 연도 뒤 1자리"
                    />
                </div>

                <span className="bar px-5">-</span>
                <div className="second-area input02">
                    {/* <InputField
                        ref={ifRef2}
                        inputMode="numeric"
                        maxLength={4}
                        value={license3}
                        onChange={(e) => {
                            setLicense3(e.target.value)

                            if (e.target.value.length >= 4) {
                                ifRef3.current?.focus()
                            }

                            handleChanged()
                        }}
                        title="일련번호 앞 4자리"
                    /> */}
                    <InputSecureField
                        id="dl_md2"
                        ref={ifRef2}
                        value={license3}
                        maxLength={6}
                        onChange={handleChanged}
                        onClose={(value, isFulfilled) => {
                            handleClose()
                            if (isFulfilled) {
                                ifRef3.current?.focus()
                            }
                        }}
                        title="일련번호 6자리"
                    />
                </div>
                <span className="bar px-5">-</span>
                <div className="second-area input03">
                    <InputSecureField
                        id="dl_ed"
                        ref={ifRef3}
                        value={license4}
                        maxLength={1}
                        onChange={handleChanged}
                        onClose={(value, isFulfilled) => {
                            handleClose()
                            if (isFulfilled) {
                                ifRef4.current?.focus()
                            }
                        }}
                        title="체크섬 1자리"
                    />
                    <InputField
                        ref={ifRef4}
                        inputMode="numeric"
                        maxLength={1}
                        value={license5}
                        onChange={(e) => {
                            setLicense5(e.target.value)
                            handleChanged(null, null, e.target.value)
                        }}
                        title="발급회차 1자리"
                    />
                </div>
            </div>
            {resultCheck}
        </>
    )
})
